<template>
  <v-container
    :class="containerClasses"
    class="page-view__container ma-0 pa-0"
    fluid
    v-on="$listeners"
  >
    <!-- header -->
    <div v-if="!isLoading" class="page-view__header">
      <template>
        <slot name="header" />
      </template>
    </div>

    <!-- menu::secondary -->
    <nav v-if="hasTabs && !isLoading" class="page-view__nav">
      <v-tabs
        v-if="!isLoading"
        v-model="nav.secondary"
        :vertical="!isLowerScreen"
        class="page-view__tabs--vertical"
        @change="tab"
      >
        <v-tab
          v-for="({ title, to, href, addBetaTag }, index) in menu"
          :key="index"
          :class="{
            'v-tab--active':
              !~$route.path.indexOf(nav.secondary) && index === 0,
          }"
          :center-active="isLowerScreen"
          :show-arrows="false"
          :href="href"
          :to="to"
        >
          <span>
            {{ title }}
            <sup
              v-if="to === '/settings/management' || addBetaTag"
              class="pl-1 turquisePanda--text font-weight-normal"
              >BETA</sup
            ></span
          >
        </v-tab>
      </v-tabs>
    </nav>

    <!-- pre-content -->
    <slot name="preContent" />
    <!-- filter -->
    <div v-if="hasFilter && !isLoading" class="page-view__filter">
      <div>
        <slot name="filter" />
      </div>
    </div>

    <!-- content -->
    <div
      class="page-view__content"
      v-bind="$attrs"
      :style="{ height: contentHeight }"
      :class="{ 'mt-12': !$slots.preContent && !isMobileBreakpoint }"
    >
      <!-- content::loading -->
      <template v-if="isLoading">
        <loading-logo class="mt-16" :value="250" />
      </template>

      <!-- content::data -->
      <template v-else>
        <slot name="content" />
      </template>
    </div>

    <!-- aside -->
    <div v-if="hasAside && !isLoading" class="page-view__aside">
      <!-- content -->
      <component
        class="page-view__aside--content"
        :is="aside[nav.aside].component"
        :propsData="aside[nav.aside].props"
      />

      <!-- nav -->
      <nav class="page-view__aside--nav">
        <v-tabs v-model="nav.aside" vertical @change="tab">
          <v-tab
            v-for="({ title, icon }, index) in aside"
            :key="index"
            class="px-0"
          >
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-icon>{{ icon }}</v-icon>
                </span>
              </template>
              <span>{{ title }}</span>
            </v-tooltip>
          </v-tab>
        </v-tabs>
      </nav>
    </div>
  </v-container>
</template>

<script>
// external imports
import _ from 'lodash'

// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import { mapState } from 'vuex'

export default {
  name: 'PageView',
  components: {
    LoadingLogo,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
    contentHeight: {
      type: String,
      default: 'auto', // ou outro valor padrão
    },
    fillHeight: {
      type: Boolean,
      default: false,
      required: false,
    },
    menu: {
      type: [Array, Object],
      default: () => [],
      required: false,
    },
    aside: {
      type: [Array, Object],
      default: () => [],
      required: false,
    },
    customClass: {
      type: String,
      required: false,
      default: '',
    },
  },
  data: () => ({
    headerMenu: [],
    nav: {
      mobile: 0,
      secondary: 0,
      aside: 0,
    },
    hasFilter: false,
  }),
  computed: {
    ...mapState('user', {
      isTabletBreakpoint: 'isTabletBreakpoint',
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    hasTabs() {
      return !_.isEmpty(this.menu)
    },
    hasAside() {
      return !_.isEmpty(this.aside)
    },
    isLowerScreen() {
      return window.innerWidth < 680
    },
    containerClasses: {
      get() {
        if (this.isLoading) return 'loading'
        const options = [
          this.hasTabs ? 'a' : '',
          this.hasFilter ? 'b' : '',
          this.fillHeight && !this.isLoading ? 'c' : '',
          this.hasAside ? 'd' : '',
          _.has(this.$slots, 'preContent') ? ' has-pre-content' : '',
        ]

        return options.join('')
      },
      cache: false,
    },

    additionalClasses() {
      const finalClass = []
      if (this.customClass) finalClass.push(this.customClass)
      if (_.has(this.$slots, 'preContent')) {
        finalClass.push('mt-6')
      }
      return finalClass
    },
  },
  created() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.update()
      })
    })
  },
  mounted() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.update()
      })
    })
  },
  methods: {
    emit({ name, value }) {
      this.$emit(name, value)
    },
    tab(value) {
      this.emit({ name: 'tab', value })
    },
    tabMobile(value) {
      this.emit({ name: 'tab-mobile', value })
    },
    update() {
      this.hasFilter = _.has(this.$slots, 'filter')
      this.headerMenu =
        this.$slots?.header[0]?.componentOptions?.propsData?.menu || []
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_tabsV2';

.page-view {
  &__container {
    display: grid;
    height: 100%;
    background-color: #f7f7f8;

    grid-template-areas: 'header-mobile' 'content' 'header';
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 1fr;

    @media (min-width: 680px) {
      grid-template-areas: 'header' 'content';
      grid-template-rows: 76px 1fr;
    }

    // loading
    &.loading {
      grid-template-areas: 'content';
      grid-template-rows: 1fr;
    }

    // (only nav) OR (nav && 100vh)
    &.a,
    &.ac {
      grid-template-areas:
        'header-mobile'
        'nav'
        'content'
        'header';
      grid-template-rows: auto auto 1fr auto;
      grid-template-columns: 1fr;

      @media (min-width: 680px) {
        grid-template-areas:
          'header header'
          'nav content';
        grid-template-rows: 76px 1fr;
        grid-template-columns: auto 1fr;
      }
    }

    // only filter
    &.b {
      grid-template-areas:
        'header-mobile'
        'filter'
        'content'
        'header';
      grid-template-rows: auto auto 1fr auto;
      grid-template-columns: 1fr;

      @media (min-width: 680px) {
        grid-template-areas:
          'header'
          'filter'
          'content';
        grid-template-rows: 76px auto 1fr;
        grid-template-columns: 1fr;
      }
    }

    // (only 100vh) OR (nav && 100vh)
    &.c,
    &.ac {
      .page-view__content {
        overflow-y: hidden;
      }

      @media (min-width: 680px) {
        overflow: auto;
      }
    }

    // nav && filter
    &.ab {
      grid-template-areas:
        'header-mobile'
        'nav'
        'filter'
        'content'
        'header';
      grid-template-rows: auto auto auto 1fr auto;
      grid-template-columns: 1fr;

      @media (min-width: 680px) {
        grid-template-areas:
          'header header'
          'nav filter'
          'nav content';
        grid-template-rows: 76px auto 1fr;
        grid-template-columns: auto 1fr;
      }
    }

    // only aside
    &.d {
      grid-template-areas:
        'header header'
        'content aside';
      grid-template-rows: 76px 1fr;
      grid-template-columns: 1fr 500px;

      height: 100vh;

      .page-view__content {
        margin: 0;

        background-color: rgba(#fff, 0);
        border: none;

        overflow-y: auto;
      }
      @media (max-width: 1350px) {
        grid-template-columns: 1fr 400px; // Altere para o valor desejado para telas menores
      }

      @media (min-width: 1658px) {
        grid-template-columns: 1fr 620px; // Altere para o valor desejado para telas menores
      }
    }

    // all
    &.abc {
      grid-template-areas:
        'header-mobile'
        'nav'
        'filter'
        'content'
        'header';
      grid-template-rows: auto auto auto 1fr auto;
      grid-template-columns: 1fr;

      .page-view__content {
        overflow-y: hidden;
      }

      @media (min-width: 680px) {
        grid-template-areas:
          'header header'
          'nav filter'
          'nav content';
        grid-template-rows: 76px 50px calc(100vh - 125px);
        grid-template-columns: auto 1fr;
      }
    }
  }

  &__header {
    &--mobile {
      grid-area: header-mobile;

      position: relative;
      top: -1px;

      display: block;

      @media (min-width: 680px) {
        display: none;
      }
    }
  }

  &__nav {
    grid-area: nav;
    margin: {
      top: $spacing-base;
      left: $spacing-minus-2;
      right: 25px;
    }

    width: calc(100vw - #{$spacing-minus-2 * 2});
    overflow-x: scroll;

    @media (min-width: 680px) {
      margin: {
        top: $spacing-plus-2;
        left: $spacing-plus-1;
        right: 25px;
      }

      width: auto;
      overflow-x: initial;
    }
  }

  &__tabs {
    @include v-tabs();

    &--vertical {
      @include v-tabs($vertical: true);
    }
  }

  &__filter {
    grid-area: filter;
    margin-top: $spacing-base;

    display: flex;

    justify-content: center;
    align-items: center;

    @media (min-width: 680px) {
      margin-top: $spacing-plus-2;
    }
  }

  &__content {
    grid-area: content;

    margin: 0;
    background-color: var(--v-neutral-lighten4);
    padding: $spacing-base 8px;
    border-radius: 8px;
    height: 100vh;
    overflow-y: auto;

    @media (min-width: 680px) {
      margin: $spacing-plus-1 $spacing-plus-1;
      width: initial;
      overflow-x: hidden;
    }
  }

  &__aside {
    grid-area: aside;

    display: grid;
    height: 100%;
    border-left: 1px solid var(--v-neutral-lighten3);
    background-color: var(--v-neutral-lighten4);

    grid-template-columns: 1fr 80px;
    grid-template-rows: 1fr;
    grid-template-areas: 'content nav';
    overflow: hidden;

    &--content {
      grid-area: content;

      padding: $spacing-base;
      overflow: auto;
    }

    &--nav {
      $size: 48px;

      grid-area: nav;

      display: flex;
      width: 100%;
      border-left: 1px solid var(--v-neutral-lighten3);
      padding: $spacing-base 0;

      align-items: flex-start;
      justify-content: center;

      &::v-deep {
        .v-tabs-slider-wrapper {
          display: none;
        }

        .v-tab {
          margin: 0 auto;
          width: $size;
          min-width: $size;
          max-width: $size;
        }
      }
    }
  }
}

.bg-dark {
  .page-view__aside--nav {
    background-color: var(--v-neutral-darken2);
  }

  .page-view {
    &__container {
      background-color: varFigma(neutral-background);
    }

    &__content {
      background-color: varFigma(neutral-100-white);
      border-color: var(--v-neutral-darken1);
    }
  }
}
</style>
